/**
 * QMS Client Ticket Wizard
 * Simple single-step creation form for client tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Send } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useCreateQmsTicket } from '../../hooks/useQmsTicket';
import { QmsPrioritySelect } from '../shared/QmsFormFields';
import { QmsAccountSelect } from '../shared/QmsAccountSelect';
import { QmsUserSelect } from '../shared/QmsUserSelect';
import { QmsRichTextEditor } from '../shared/QmsRichTextEditor';
import { getQmsOrganisationId } from '../../utils/organisation';
import { parseQmsTags } from '../../utils/tags';
import type { QmsTicket, QmsTicketPayload, QmsPriority } from '../../types/qms.types';

interface QmsClientTicketWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated: (ticket: QmsTicket) => void;
}

interface ClientFormData {
  title: string;
  description: string;
  priority: QmsPriority;
  account_id?: string;
  assigned_to?: string;
  due_date: string;
  // Contact
  user_name: string;
  email: string;
  phone: string;
  // Financial
  quote_number: string;
  purchase_order: string;
  value: string;
  // Scoring
  important: number | undefined;
  urgent: number | undefined;
  // Notes
  internal_notes: string;
  tags: string[];
}

const initialFormData: ClientFormData = {
  title: '',
  description: '',
  priority: 3,
  account_id: undefined,
  assigned_to: undefined,
  due_date: '',
  user_name: '',
  email: '',
  phone: '',
  quote_number: '',
  purchase_order: '',
  value: '',
  important: undefined,
  urgent: undefined,
  internal_notes: '',
  tags: [],
};

export function QmsClientTicketWizard({ isOpen, onClose, onTicketCreated }: QmsClientTicketWizardProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const createMutation = useCreateQmsTicket();

  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [tagsInput, setTagsInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = t('validation.titleRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): QmsTicketPayload => {
    const tags = parseQmsTags(tagsInput);

    return {
      type: 'client',
      title: formData.title,
      description: formData.description || undefined,
      priority: formData.priority,
      status: 'client_submitted',
      organisation: getQmsOrganisationId(user?.organisation),
      account_id: formData.account_id || undefined,
      assigned_to: formData.assigned_to || undefined,
      due_date: formData.due_date || undefined,
      user_name: formData.user_name || undefined,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      quote_number: formData.quote_number || undefined,
      purchase_order: formData.purchase_order || undefined,
      value: formData.value ? Number(formData.value) : undefined,
      important: formData.important,
      urgent: formData.urgent,
      internal_notes: formData.internal_notes || undefined,
      tags: tags.length > 0 ? tags : undefined,
    } as QmsTicketPayload;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitError(null);

    try {
      const payload = buildPayload();
      const ticket = await createMutation.mutateAsync(payload);
      toast.success(t('messages.ticketCreated'));
      onTicketCreated(ticket);
      handleClose();
    } catch (error) {
      console.error('Failed to create client ticket:', error);
      setSubmitError(error instanceof Error ? error.message : t('errors.savingTicket'));
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setTagsInput('');
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl transform transition-transform">
          <div className="flex h-full flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="bg-emerald-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {t('wizard.createClientTicket', 'Create Client Ticket')}
                </h2>
                <button onClick={handleClose} className="rounded-lg p-2 text-white hover:bg-emerald-700 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.fields.title')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder={t('form.placeholders.title')}
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.description')}</label>
                <QmsRichTextEditor
                  value={formData.description}
                  onChange={(html) => setFormData({ ...formData, description: html })}
                  placeholder={t('form.placeholders.description')}
                />
              </div>

              {/* Priority, Account, Assigned To */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.priority')}</label>
                  <QmsPrioritySelect value={formData.priority} onChange={(value) => setFormData({ ...formData, priority: value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.account')}</label>
                  <QmsAccountSelect value={formData.account_id} onChange={(accountId) => setFormData({ ...formData, account_id: accountId })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.assignedTo')}</label>
                  <QmsUserSelect value={formData.assigned_to} onChange={(u) => setFormData({ ...formData, assigned_to: u?._id })} placeholder={t('form.placeholders.selectAssignee')} />
                </div>
              </div>

              {/* Important, Urgent, Due Date */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.important', 'Important')}</label>
                  <select value={formData.important ?? ''} onChange={(e) => setFormData({ ...formData, important: e.target.value ? Number(e.target.value) : undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option value="">-</option>
                    {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.urgent', 'Urgent')}</label>
                  <select value={formData.urgent ?? ''} onChange={(e) => setFormData({ ...formData, urgent: e.target.value ? Number(e.target.value) : undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option value="">-</option>
                    {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.dueDate')}</label>
                  <input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('form.sections.contactInfo', 'Contact Information')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.contactName', 'Contact Name')}</label>
                    <input type="text" value={formData.user_name} onChange={(e) => setFormData({ ...formData, user_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.contactEmail', 'Email')}</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.phone', 'Phone')}</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </div>
              </div>

              {/* Financial */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('form.sections.financial', 'Financial')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.quoteNumber', 'Quote #')}</label>
                    <input type="text" value={formData.quote_number} onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.purchaseOrder', 'PO #')}</label>
                    <input type="text" value={formData.purchase_order} onChange={(e) => setFormData({ ...formData, purchase_order: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.value', 'Value')}</label>
                    <input type="number" step="0.01" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </div>
              </div>

              {/* Tags & Internal Notes */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.tags')}</label>
                    <input
                      type="text"
                      value={tagsInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTagsInput(value);
                        setFormData({ ...formData, tags: parseQmsTags(value) });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder={t('form.placeholders.tags')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fields.internalNotes', 'Internal Notes')}</label>
                    <input type="text" value={formData.internal_notes} onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-4 bg-gray-50">
              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors">
                  {t('actions.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createMutation.isPending}
                  className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {createMutation.isPending ? t('wizard.creating') : t('wizard.createAndSubmit', 'Create & Submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
